import React, { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, MessageCircle, Home, Loader2 } from "lucide-react";
import { useForm } from "../../context/FormContext";
import { CONTACT, USE_OPTIONS, DURATION_BY_USE, waNumberLink } from "../../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTCH_OPTIONS = [
  "Yes, please send me more information",
  "Maybe, I would like to see the options",
  "No, thank you",
];

const USE_DESCRIPTIONS = {
  "Workshop or Class": "Educational or professional instruction without vendors or event production.",
  "Movement or Private Class": "Instructor-led Pilates, yoga, dance, modeling or rehearsal without vendors, sponsors or event elements.",
  "Curated Brand Experience": "An experience involving vendors, sponsors, branded displays, refreshments, ticket sales, product demonstrations or additional production.",
};

const empty = {
  selected_use: "",
  activity_description: "",
  preferred_date: "",
  alternative_date: "",
  preferred_start_time: "",
  start_time_flexible: "",
  duration: "",
  expected_attendees: "",
  configuration: "",
  paid_attendance: "",
  outside_vendors: "",
  special_requirements: "",
  notch_coffee_interest: "",
  full_name: "",
  business: "",
  email: "",
  phone: "",
  instagram_website: "",
  agreement_accepted: false,
  company_website: "",
  cta_source: "",
  tags: [],
};

const inputCls =
  "w-full bg-cream border-2 border-charcoal/25 rounded-2xl px-4 py-3 text-charcoal text-sm focus:border-berry focus:outline-none transition-colors placeholder:text-charcoal/40";
const labelCls = "block overline text-berry mb-2";

function Pills({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          className={`btn px-4 py-2.5 text-sm border-2 ${
            value === o ? "bg-berry text-cream border-berry" : "bg-cream border-charcoal/25 text-charcoal/80 hover:border-berry"
          }`}
        >
          {o}
        </button>
      ))}
    </div>
  );
}

export function AvailabilityForm({ formRef }) {
  const { prefill } = useForm();
  const [step, setStep] = useState(1);
  const [data, setData] = useState(empty);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  useEffect(() => {
    if (!prefill) return;
    setData((d) => {
      const next = { ...d };
      if (prefill.use) next.selected_use = prefill.use;
      if (prefill.duration) next.duration = prefill.duration;
      if (prefill.notch) next.notch_coffee_interest = NOTCH_OPTIONS[0];
      if (prefill.tag) next.tags = Array.from(new Set([...(d.tags || []), prefill.tag]));
      next.cta_source = prefill.tag || (prefill.notch ? "notch_coffee" : prefill.use || "request_availability");
      return next;
    });
    setDone(false);
    setStep(1);
  }, [prefill]);

  const durations = data.selected_use ? DURATION_BY_USE[data.selected_use] : [];

  const validateStep = () => {
    setError("");
    if (step === 1) {
      if (!data.selected_use) return "Please choose how you’d like to use the space.";
      if (!data.activity_description.trim()) return "Please tell us a little about your idea.";
    }
    if (step === 2) {
      if (!data.preferred_date) return "Please select a preferred date.";
      if (!data.duration) return "Please select a duration.";
    }
    if (step === 4) {
      if (!data.full_name.trim()) return "Please enter your name.";
      if (!data.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) return "Please enter a valid email.";
      if (!data.phone.trim()) return "Please enter your phone number.";
      if (!data.agreement_accepted) return "Please confirm the usage agreement to continue.";
    }
    return "";
  };

  const next = () => {
    const e = validateStep();
    if (e) { setError(e); return; }
    setStep((s) => Math.min(4, s + 1));
  };
  const back = () => { setError(""); setStep((s) => Math.max(1, s - 1)); };

  const submit = async () => {
    const e = validateStep();
    if (e) { setError(e); return; }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/leads`, data);
      setDone(true);
      if (formRef?.current) formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const waMessage = `Hi! I just submitted an availability request for DMEDIA CLUB. My name is ${data.full_name || "[Name]"}, and I’m interested in using the space for ${data.selected_use || "[Use]"} on ${data.preferred_date || "[Date]"}.`;

  if (done) {
    return (
      <section id="request" ref={formRef} className="bg-berry text-cream py-20 md:py-28" data-testid="confirmation-section">
        <div className="max-w-[760px] mx-auto px-5 md:px-10 text-center">
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.6 }}
            className="w-16 h-16 mx-auto mb-8 rounded-full bg-butter border-2 border-charcoal flex items-center justify-center">
            <Check size={28} strokeWidth={2.2} className="text-charcoal" />
          </motion.div>
          <h2 className="display text-4xl sm:text-6xl leading-[0.95]">Your Idea Is in Our Hands.</h2>
          <p className="mt-8 text-base md:text-lg text-offwhite/75 leading-relaxed">
            Thank you for your interest in creating at DMEDIA CLUB. We received your request, and one of
            our representatives will review the details and follow up within one business day with
            availability and next steps.
          </p>
          <p className="mt-5 text-sm text-offwhite/55 leading-relaxed">
            Your reservation is not confirmed until the activity is approved, the agreement is completed,
            and full payment is received.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              data-testid="confirm-whatsapp-btn"
              href={waNumberLink(waMessage)}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-8 py-4 text-sm tracking-wide hover:brightness-95 transition"
            >
              <MessageCircle size={17} strokeWidth={1.6} /> Continue on WhatsApp
            </a>
            <button
              data-testid="confirm-home-btn"
              onClick={() => { setDone(false); setData(empty); setStep(1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="inline-flex items-center justify-center gap-2 border border-offwhite/30 px-8 py-4 text-sm tracking-wide hover:bg-offwhite/10 transition"
            >
              <Home size={17} strokeWidth={1.6} /> Return to Home
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="request" ref={formRef} className="bg-offwhite text-charcoal py-28 md:py-36" data-testid="form-section">
      <div className="max-w-[860px] mx-auto px-5 md:px-10">
        <p className="overline text-plum">Request Availability</p>
        <h2 className="display text-4xl sm:text-6xl mt-5 leading-[0.95]">What Do You Want to Create?</h2>
        <p className="mt-5 text-base md:text-lg text-charcoal/70">
          Tell us about your idea, and we’ll help determine the best way to bring it into the space.
        </p>
        <p className="mt-3 text-xs text-charcoal/50">
          Every request is personally reviewed. One of our representatives will follow up within one business day.
        </p>

        {/* progress */}
        <div className="mt-10 flex gap-2" data-testid="form-progress">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className={`h-1 flex-1 transition-colors duration-500 ${s <= step ? "bg-plum" : "bg-charcoal/15"}`} />
          ))}
        </div>
        <p className="mt-3 text-xs uppercase tracking-widest text-charcoal/50">Step {step} of 4</p>

        {/* honeypot */}
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={data.company_website}
          onChange={(e) => set("company_website", e.target.value)}
          className="hidden"
          aria-hidden="true"
        />

        <div className="mt-8 min-h-[300px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.4 }}
            >
              {step === 1 && (
                <div className="space-y-8">
                  <div>
                    <label className={labelCls}>How would you like to use the creative space?</label>
                    <Pills options={USE_OPTIONS} value={data.selected_use} testid="form-use-options"
                      onChange={(v) => { set("selected_use", v); set("duration", ""); }} />
                    <div className="mt-4 space-y-2">
                      {USE_OPTIONS.map((o) => (
                        <p key={o} className="text-xs text-charcoal/60 leading-relaxed">
                          <span className="text-charcoal/80 font-medium">{o}:</span> {USE_DESCRIPTIONS[o]}
                        </p>
                      ))}
                    </div>
                    <p className="mt-4 text-xs text-plum leading-relaxed border-l-2 border-plum pl-4">
                      Activities that include vendors are reviewed and priced as Curated Brand
                      Experiences, even when the primary activity is Pilates, yoga, movement or a workshop.
                    </p>
                  </div>
                  <div>
                    <label className={labelCls}>Tell us a little about what you are planning.</label>
                    <textarea data-testid="form-description" rows={4} className={inputCls}
                      placeholder="Describe your workshop, class, session, or brand idea…"
                      value={data.activity_description} onChange={(e) => set("activity_description", e.target.value)} />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-8">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className={labelCls}>Preferred date</label>
                      <input data-testid="form-preferred-date" type="date" className={inputCls}
                        value={data.preferred_date} onChange={(e) => set("preferred_date", e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls}>Alternative date</label>
                      <input data-testid="form-alt-date" type="date" className={inputCls}
                        value={data.alternative_date} onChange={(e) => set("alternative_date", e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls}>Preferred start time</label>
                      <input data-testid="form-start-time" type="time" className={inputCls}
                        value={data.preferred_start_time} onChange={(e) => set("preferred_start_time", e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls}>Is your start time flexible?</label>
                      <Pills options={["Yes", "No"]} value={data.start_time_flexible} onChange={(v) => set("start_time_flexible", v)} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Duration</label>
                    {data.selected_use ? (
                      <Pills options={durations} value={data.duration} testid="form-duration" onChange={(v) => set("duration", v)} />
                    ) : (
                      <p className="text-sm text-charcoal/50">Please select how you’ll use the space in Step 1 first.</p>
                    )}
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-8">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className={labelCls}>Expected number of attendees</label>
                      <input data-testid="form-attendees" type="number" min="1" max="30" className={inputCls}
                        placeholder="Approx. number of people" value={data.expected_attendees} onChange={(e) => set("expected_attendees", e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls}>Preferred configuration</label>
                      <Pills options={["Classroom", "Open Floor", "Not Sure Yet"]} value={data.configuration} onChange={(v) => set("configuration", v)} />
                    </div>
                    <div>
                      <label className={labelCls}>Will participants pay to attend?</label>
                      <Pills options={["Yes", "No", "Not Sure"]} value={data.paid_attendance} onChange={(v) => set("paid_attendance", v)} />
                    </div>
                    <div>
                      <label className={labelCls}>Are outside vendors involved?</label>
                      <Pills options={["Yes", "No", "Not Sure"]} value={data.outside_vendors} onChange={(v) => set("outside_vendors", v)} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Special requirements (optional)</label>
                    <textarea rows={3} className={inputCls} placeholder="Anything we should know?"
                      value={data.special_requirements} onChange={(e) => set("special_requirements", e.target.value)} />
                  </div>
                  <div>
                    <label className={labelCls}>Interested in personalized iced lattes from NØTCH COFFEE?</label>
                    <Pills options={NOTCH_OPTIONS} value={data.notch_coffee_interest} testid="form-notch" onChange={(v) => set("notch_coffee_interest", v)} />
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <label className={labelCls}>Full name</label>
                      <input data-testid="form-name" className={inputCls} value={data.full_name} onChange={(e) => set("full_name", e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls}>Business or organization</label>
                      <input data-testid="form-business" className={inputCls} value={data.business} onChange={(e) => set("business", e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls}>Email address</label>
                      <input data-testid="form-email" type="email" className={inputCls} value={data.email} onChange={(e) => set("email", e.target.value)} />
                    </div>
                    <div>
                      <label className={labelCls}>Phone number</label>
                      <input data-testid="form-phone" type="tel" className={inputCls} value={data.phone} onChange={(e) => set("phone", e.target.value)} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Instagram or website (optional)</label>
                    <input data-testid="form-social" className={inputCls} value={data.instagram_website} onChange={(e) => set("instagram_website", e.target.value)} />
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer pt-2">
                    <input data-testid="form-agreement" type="checkbox" className="mt-1 w-4 h-4 accent-[#7C0D46]"
                      checked={data.agreement_accepted} onChange={(e) => set("agreement_accepted", e.target.checked)} />
                    <span className="text-sm text-charcoal/70 leading-relaxed">
                      I understand that DMEDIA CLUB is a creative space for approved workshops, classes,
                      movement sessions, business training, professional presentations, and curated brand
                      experiences. Parties and social celebrations are not permitted.
                    </span>
                  </label>
                  <p className="text-xs text-charcoal/55 leading-relaxed border-l-2 border-plum pl-4">
                    Vendors, ticketed attendance and commercial sales require advance review and may
                    affect capacity, minimum reservation time, insurance requirements and pricing.
                  </p>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {error && <p className="mt-6 text-sm text-plum" data-testid="form-error">{error}</p>}

        <div className="mt-10 flex items-center justify-between">
          <button data-testid="form-back-btn" onClick={back} disabled={step === 1}
            className={`inline-flex items-center gap-2 text-sm ${step === 1 ? "opacity-0 pointer-events-none" : "text-charcoal/70 hover:text-plum"}`}>
            <ArrowLeft size={16} strokeWidth={1.6} /> Back
          </button>
          {step < 4 ? (
            <button data-testid="form-next-btn" onClick={next}
              className="btn group inline-flex items-center gap-2 bg-berry text-cream border-2 border-berry px-8 py-4 text-sm hover:bg-berry-deep shadow-[3px_3px_0_#2A1020]">
              Continue <ArrowRight size={16} strokeWidth={1.6} className="group-hover:translate-x-0.5 transition-transform" />
            </button>
          ) : (
            <button data-testid="form-submit-btn" onClick={submit} disabled={submitting}
              className="btn inline-flex items-center gap-2 bg-berry text-cream border-2 border-berry px-8 py-4 text-sm hover:bg-berry-deep shadow-[3px_3px_0_#2A1020] disabled:opacity-60">
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : "Request Availability"}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}
